<!-- This widget visualizes a bunch of price-related fields together. -->

<template>
  <v-form
    ref="form"
    class="px-2"
    @submit.prevent
  >
    <!-- Optional warning messages -->
    <v-layout
      v-for="(warning, warningIndex) in warnings"
      :key="'widget-field-warning-' + warningIndex"
      align-center
      class="mb-2"
    >
      <v-icon
        color="warning"
        class="mr-2"
      >
        error_outline
      </v-icon>
      <span class="warning--text">
        {{ warning.prefix }}: <b>{{ warning.fieldLabel }}</b>
      </span>
    </v-layout>
    <v-slide-y-reverse-transition>
      <v-layout
        v-show="priceField.value"
        column
      >
        <!-- Rebate enabled -->
        <overlay-data-boolean-field
          :field="rebateEnabledField"
          :value.sync="rebateEnabledField.value"
          :disabled="!hasValidRebate"
          hide-details
          @update:value="$emit('changed')"
        />
        <v-slide-y-transition>
          <!-- Rebate subline -->
          <overlay-data-text-field
            v-show="hasValidRebate && rebateEnabledField.value"
            :field="rebateSublineField"
            :value.sync="rebateSublineField.value"
            @update:value="$emit('changed')"
          />
        </v-slide-y-transition>
      </v-layout>
    </v-slide-y-reverse-transition>
    <v-layout column>
      <v-slide-y-reverse-transition>
        <!-- Old price -->
        <v-text-field
          v-show="priceField.value"
          v-model="oldPriceField.value"
          :type="numberFieldInputType"
          :step="0.01"
          :label="oldPriceField.data.label"
          :placeholder="oldPriceField.data.placeholder"
          :required="oldPriceField.required"
          :rules="oldPriceField.rules"
          data-cy="overlay-data-pricefield"
          class="
            pt-4
            px-3
            pb-2
            mt-0
            overlay-options-field-bg
            old-price
            rotate-clear-icon-to-look
          "
          clearable
          clear-icon="add_circle"
          @focus="onFieldInputFocused(oldPriceField, $event)"
          @input="$emit('changed')"
        />
      </v-slide-y-reverse-transition>
      <v-layout
        align-baseline
        class="overlay-options-field-bg"
      >
        <v-slide-x-reverse-transition>
          <!-- Price prefix -->
          <overlay-data-text-field
            v-show="priceField.value"
            :field="pricePrefixField"
            :value.sync="pricePrefixField.value"
            background-class=""
            style="max-width: 140px"
            @update:value="$emit('changed')"
          />
        </v-slide-x-reverse-transition>
        <!-- Price -->
        <v-text-field
          v-model="priceField.value"
          :type="numberFieldInputType"
          :step="0.01"
          :label="priceField.data.label"
          :placeholder="priceField.data.placeholder"
          :required="priceField.required"
          :rules="priceField.rules"
          data-cy="overlay-data-pricefield"
          class="pt-4 px-3 pb-2 mt-0 price"
          @focus="onFieldInputFocused(priceField, $event)"
          @input="$emit('changed')"
        />
      </v-layout>
      <v-slide-y-transition>
        <!-- Price subline -->
        <overlay-data-text-field
          v-show="priceField.value"
          :field="priceSublineField"
          :value.sync="priceSublineField.value"
          @update:value="$emit('changed')"
        />
      </v-slide-y-transition>
    </v-layout>
  </v-form>
</template>

<script>
import {transformPriceValueToUseNumberOfDecimals} from '@/utils';

import {
  ensureDotSeparator,
  getNumDecimals,
  isPriceField,
  OverlayOptionsField,
  OverlayOptionsFieldWarning,
} from '@/mixins/overlayOptions.js';

import OverlayDataBooleanField from '@/components/OverlayDataBooleanField';
import OverlayDataTextField from '@/components/OverlayDataTextField';

export default {
  components: {
    OverlayDataBooleanField,
    OverlayDataTextField,
  },
  props: {
    /**
     * Name of the widget as used in overlay options.
     *
     * Needed to backtrack validation status to its group.
     */
    name: {
      type: String,
      required: true,
    },
    oldPrice: {
      type: OverlayOptionsField,
      required: true,
    },
    pricePrefix: {
      type: OverlayOptionsField,
      required: true,
    },
    priceSubline: {
      type: OverlayOptionsField,
      required: true,
    },
    price: {
      type: OverlayOptionsField,
      required: true,
    },
    rebateEnabled: {
      type: OverlayOptionsField,
      required: true,
    },
    rebateSubline: {
      type: OverlayOptionsField,
      required: true,
    },
    /**
     * Optional warning messages shown above the interface.
     */
    warnings: {
      type: Array[OverlayOptionsFieldWarning],
      default: null,
    },
  },
  data() {
    // We have to mirror the props as data attributes so they are reactive.
    // See: https://vuejs.org/v2/guide/components-props.html#One-Way-Data-Flow
    return {
      oldPriceField: this.oldPrice,
      priceField: this.price,
      pricePrefixField: this.pricePrefix,
      priceSublineField: this.priceSubline,
      rebateEnabledField: this.rebateEnabled,
      rebateSublineField: this.rebateSubline,
    };
  },
  computed: {
    hasValidRebate() {
      return (
        !!this.priceField.value &&
        !!this.oldPriceField.value &&
        this.oldPriceField.value - this.priceField.value > 0
      );
    },
    /**
     * IE11/Edge do not support using a comma separator in number inputs.
     *
     * The only workaround for this is to use type="text" instead.
     * We'll lose the increase/decrease buttons, but all rule
     * validations will still work and german users can use a comma as
     * they are used to.
     */
    numberFieldInputType() {
      if (this.$store.state.common.runtimeEnv.edge) {
        return 'text';
      }
      return 'number';
    },
    fields() {
      return [
        this.oldPriceField,
        this.pricePrefixField,
        this.priceSublineField,
        this.priceField,
        this.rebateEnabledField,
        this.rebateSublineField,
      ];
    },
  },
  created() {
    this.registerSetValueFromOutsideEventListener();
  },
  methods: {
    onFieldInputFocused(field, event) {
      if (field && field.data && field.data.select_on_focus) {
        event.target.select();
      }
    },
    validate() {
      return this.$refs.form.validate();
    },
    registerSetValueFromOutsideEventListener() {
      this.$bus.$on('set-overlay-data-field-value', (fieldName, newValue) => {
        for (const field of this.fields) {
          if (field.name !== fieldName) {
            continue;
          }
          if (isPriceField(field)) {
            const numDecimals = getNumDecimals(field.schema.multipleOf || 0.01);
            newValue = transformPriceValueToUseNumberOfDecimals(
              newValue,
              numDecimals
            );
          }
          field.value = newValue;
        }
      });
    },
    getOverlayData() {
      const widgetOverlayData = {
        _labels: {},
      };
      for (const field of this.fields) {
        let value = field.value;
        if (isPriceField(field)) {
          value = ensureDotSeparator(value);
          value = Number(value);
        }

        widgetOverlayData[field.name] = value;
        widgetOverlayData._labels[field.name] = field.label;
      }
      return widgetOverlayData;
    },
  },
};
</script>

<style scoped>
.old-price >>> input {
  font-size: 1.25em;
  min-height: 1.5em;
}

.price >>> input {
  font-weight: bold;
  font-size: 2em;
  min-height: 1.5em;
}

.rotate-clear-icon-to-look >>> .v-input__icon--clear {
  transform: rotateZ(45deg);
}
</style>



// WEBPACK FOOTER //
// src/components/OverlayDataWidgetPrice.vue